import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { Eye, EyeOff, Loader2, Check, X } from 'lucide-react'

export default function RegisterPage() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [acceptTerms, setAcceptTerms] = useState(false)
  const { register, isLoading, error, clearError } = useAuth()
  const navigate = useNavigate()

  // Password validation (must match Cognito policy: 12+ chars, lower, upper, digit, symbol)
  const passwordChecks = {
    length: password.length >= 12,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    symbol: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  }
  const passwordValid = Object.values(passwordChecks).every(Boolean)
  const passwordsMatch = password === confirmPassword && password.length > 0

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()
    
    if (!passwordValid || !passwordsMatch || !acceptTerms) {
      return
    }
    
    try {
      await register(email, password, name)
      navigate('/verify', { state: { email } })
    } catch (err) {
      // Error is handled by the hook
    }
  }

  const PasswordCheck = ({ valid, label }: { valid: boolean; label: string }) => (
    <div className="flex items-center space-x-2 text-sm">
      {valid ? (
        <Check className="w-4 h-4 text-green-500" />
      ) : (
        <X className="w-4 h-4 text-gray-300" />
      )}
      <span className={valid ? 'text-green-600' : 'text-gray-500'}>{label}</span>
    </div>
  )

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 animate-fade-in">
      {/* Mobile logo */}
      <div className="lg:hidden text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-500 rounded-2xl mb-4">
          <span className="text-3xl">💬</span>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">ReplyForge</h1>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Create your account</h2>
        <p className="text-gray-600 mt-1">Start your 14-day free trial</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Full name
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors"
            placeholder="John Doe"
            required
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            Work email
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors"
            placeholder="name@company.com"
            required
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors pr-12"
              placeholder="••••••••"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          
          {password && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg grid grid-cols-2 gap-2">
              <PasswordCheck valid={passwordChecks.length} label="12+ characters" />
              <PasswordCheck valid={passwordChecks.lowercase} label="Lowercase" />
              <PasswordCheck valid={passwordChecks.uppercase} label="Uppercase" />
              <PasswordCheck valid={passwordChecks.number} label="Number" />
              <PasswordCheck valid={passwordChecks.symbol} label="Symbol (!@#$...)" />
            </div>
          )}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
            Confirm password
          </label>
          <input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors ${
              confirmPassword && !passwordsMatch ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="••••••••"
            required
          />
          {confirmPassword && !passwordsMatch && (
            <p className="mt-1 text-sm text-red-600">Passwords don't match</p>
          )}
        </div>

        <div className="flex items-start">
          <input
            id="terms"
            type="checkbox"
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
            className="w-4 h-4 mt-1 border-gray-300 rounded text-yellow-500 focus:ring-yellow-500"
          />
          <label htmlFor="terms" className="ml-2 text-sm text-gray-600">
            I agree to the{' '}
            <a href="#" className="text-yellow-600 hover:text-yellow-700">Terms of Service</a>
            {' '}and{' '}
            <a href="#" className="text-yellow-600 hover:text-yellow-700">Privacy Policy</a>
          </label>
        </div>

        <button
          type="submit"
          disabled={isLoading || !passwordValid || !passwordsMatch || !acceptTerms}
          className="w-full py-3 px-4 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Creating account...
            </>
          ) : (
            'Create account'
          )}
        </button>
      </form>

      <div className="mt-8 text-center">
        <p className="text-gray-600">
          Already have an account?{' '}
          <Link to="/login" className="text-yellow-600 hover:text-yellow-700 font-medium">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
